import type { BusinessType } from '@app/shared';
import { createAdminApi, type AdminApiClient } from './admin';
import { createCabinetApi, type CabinetApiClient } from './cabinet';
import {
  createHttpTransport,
  type HttpTransport,
  type HttpTransportOptions,
  type RequestOptions,
} from './http';
import { createSessionController, type SessionController } from './session';
import type {
  BusinessPageDto,
  CancelResultDto,
  CatalogPageDto,
  CreateAppointmentInput,
  AppointmentDto,
  MyAppointmentDto,
  SessionDto,
  SlotsDto,
} from './types';

/**
 * Клиентская часть API (ТЗ §14.1). Семь операций публичного роутера плюс вход — ни одной
 * «на будущее»: неиспользуемый метод скрывает, что бэкенд такого пути не отдаёт.
 *
 * Признак `auth` у каждого вызова повторяет разметку `packages/backend/src/routes/public.ts`:
 * витрина (каталог, страница организации, слоты) открыта без подписи, всё, что трогает
 * записи, — только с ней.
 *
 * ТОЧКА РАСШИРЕНИЯ ДЛЯ КАБИНЕТА: методы `/api/cabinet/*` добавляются отдельным интерфейсом
 * (`CabinetApiClient`) поверх ТОГО ЖЕ `HttpTransport` — см. `createApiClients` ниже.
 * Транспорт общий намеренно: заголовок авторизации, разбор ошибок и обработка сети обязаны
 * быть одни на всё приложение, иначе кабинет заведёт вторую, чуть другую копию логики входа.
 */
export interface ClientApi {
  startSession(): Promise<SessionDto>;
  getCatalog(params?: CatalogParams): Promise<CatalogPageDto>;
  getBusiness(slug: string): Promise<BusinessPageDto>;
  getSlots(params: SlotsParams): Promise<SlotsDto>;
  createAppointment(input: CreateAppointmentInput): Promise<AppointmentDto>;
  confirmAppointment(id: string): Promise<AppointmentDto>;
  cancelAppointment(id: string): Promise<CancelResultDto>;
  listMyAppointments(): Promise<MyAppointmentDto[]>;
  /** Оценка завершённого визита, 1–5. Один раз на запись. */
  rateAppointment(id: string, stars: number): Promise<void>;
  /** Пожелание — бот пересылает его владельцу продукта в Telegram. */
  sendFeedback(text: string): Promise<void>;
  /** Ссылка на заведение в Mini App — то, что закодировано в QR. */
  getBusinessLink(slug: string): Promise<{ link: string }>;
}

export interface CatalogParams {
  type?: BusinessType;
  group?: string;
  /** Город и район — id из справочника `@app/shared`. Район допустим только с городом. */
  city?: string;
  district?: string;
  query?: string;
  limit?: number;
  cursor?: string;
}

export interface SlotsParams {
  slug: string;
  serviceId: string;
  staffId: string;
  /** Календарный день в поясе организации, строго `YYYY-MM-DD`. */
  date: string;
}

/**
 * Путь входа. Вынесен в константу, потому что его знают двое: сам метод и гейт
 * защищённых запросов (`withSession`), который обязан этот путь пропускать мимо себя.
 */
const SESSION_PATH = '/session';

export function createClientApi(transport: HttpTransport): ClientApi {
  return {
    /**
     * Вход: заводит строку `users` по подписи или возвращает уже заведённую (ТЗ §5).
     *
     * Тела у запроса нет и быть не может: имя, язык и `tg_id` бэкенд берёт из подписанной
     * `initData`, а не из того, что прислал клиент. Пришли мы их сами — любой мог бы
     * назваться чужим `tg_id`, ведь проверяется подписью именно строка Telegram.
     */
    startSession: () =>
      transport.request<SessionDto>({ method: 'POST', path: SESSION_PATH, auth: true }),

    // Параметры перечислены поимённо, а не отдаются спредом: так в query не уедет поле,
    // добавленное в `CatalogParams` для нужд экрана, — бэкенд на незнакомый ключ ответит
    // `err.validation`, и виноват будет не тот файл.
    getCatalog: (params) =>
      transport.request<CatalogPageDto>({
        method: 'GET',
        path: '/catalog',
        query: {
          type: params?.type,
          group: params?.group,
          city: params?.city,
          district: params?.district,
          query: params?.query,
          limit: params?.limit,
          cursor: params?.cursor,
        },
        auth: false,
      }),

    getBusiness: (slug) =>
      transport.request<BusinessPageDto>({
        method: 'GET',
        path: `/businesses/${encodeURIComponent(slug)}`,
        auth: false,
      }),

    getSlots: ({ slug, serviceId, staffId, date }) =>
      transport.request<SlotsDto>({
        method: 'GET',
        path: `/businesses/${encodeURIComponent(slug)}/slots`,
        query: { serviceId, staffId, date },
        auth: false,
      }),

    createAppointment: (input) =>
      transport.request<AppointmentDto>({
        method: 'POST',
        path: '/appointments',
        body: input,
        auth: true,
      }),

    confirmAppointment: (id) =>
      transport.request<AppointmentDto>({
        method: 'POST',
        path: `/appointments/${encodeURIComponent(id)}/confirm`,
        auth: true,
      }),

    cancelAppointment: (id) =>
      transport.request<CancelResultDto>({
        method: 'POST',
        path: `/appointments/${encodeURIComponent(id)}/cancel`,
        auth: true,
      }),

    /**
     * «Мои записи». Параметра клиента нет и быть не может: чьи это записи, решает подпись
     * (см. `actorTgId` в роутере бэкенда). Появись здесь `?clientTgId=`, чужую историю
     * визитов читал бы любой.
     */
    listMyAppointments: () =>
      transport.request<MyAppointmentDto[]>({
        method: 'GET',
        path: '/appointments/mine',
        auth: true,
      }),

    rateAppointment: (id, stars) =>
      transport.request<void>({
        method: 'POST',
        path: `/appointments/${encodeURIComponent(id)}/rating`,
        body: { stars },
        auth: true,
        empty: true,
      }),

    getBusinessLink: (slug) =>
      transport.request<{ link: string }>({
        method: 'GET',
        path: `/businesses/${encodeURIComponent(slug)}/link`,
        auth: false,
      }),

    sendFeedback: (text) =>
      transport.request<void>({
        method: 'POST',
        path: '/feedback',
        body: { text },
        auth: true,
        empty: true,
      }),
  };
}

/**
 * Гейт сессии: защищённый запрос ждёт заведённого аккаунта.
 *
 * Это ответ на вопрос «сессия не завелась, человек нажал „Записаться“ — что происходит».
 * Без гейта запрос ушёл бы на сервер, который про такого пользователя ничего не знает,
 * и клиент увидел бы `err.notFound` — то есть «организация не найдена» вместо «войти
 * не удалось». Гейт делает две вещи и обе обязательные:
 *
 *   1. ПОВТОРЯЕТ попытку входа. `ensure()` после отказа идёт на сервер заново, поэтому
 *      кнопка «Записаться» сама по себе является повтором: сеть моргнула на старте —
 *      второе нажатие уже проходит.
 *   2. ПОКАЗЫВАЕТ настоящую причину. Отказ входа пробрасывается как есть, и экран рисует
 *      его своим обычным `ErrorState` («перезапустите из чата с ботом», «сервер не
 *      отвечает»). Молчаливого 404 не остаётся (AGENTS.md, правило 7).
 *
 * Витрина (`auth: false`) гейт не проходит вовсе: каталог, страница организации и сетка
 * слотов обязаны открываться по QR-наклейке сразу, не дожидаясь входа.
 *
 * `POST /session` пропускается мимо гейта по пути — иначе вход ждал бы сам себя.
 */
export function withSession(
  transport: HttpTransport,
  session: Pick<SessionController, 'ensure'>,
): HttpTransport {
  return {
    async request<T>(options: RequestOptions): Promise<T> {
      if (options.auth && options.path !== SESSION_PATH) await session.ensure();
      return transport.request<T>(options);
    },
  };
}

/**
 * Единая сборка API-слоя приложения.
 *
 * Кабинет бизнеса добавляет сюда своё поле (`cabinet: createCabinetApi(transport)`),
 * не трогая ни транспорт, ни клиентские методы.
 */
export interface AppApi {
  readonly transport: HttpTransport;
  readonly client: ClientApi;
  /** Кабинет бизнеса. Тот же транспорт — см. комментарий к `ClientApi`. */
  readonly cabinet: CabinetApiClient;
  /** Админка платформы (только для владельца продукта). Тот же транспорт; доступ закрыт на бэкенде. */
  readonly admin: AdminApiClient;
  /**
   * Сессия приложения. Живёт здесь, а не в React-контексте рядом с провайдером, по двум
   * причинам: её ждёт гейт транспорта (React про него не знает), и экземпляр обязан быть
   * ОДИН на приложение — второй контроллер завёл бы вторую попытку входа и вторую,
   * расходящуюся с первой, картину состояния.
   */
  readonly session: SessionController;
}

export function createAppApi(options: HttpTransportOptions): AppApi {
  const transport = createHttpTransport(options);

  /*
   * Ссылка вперёд неизбежна: клиент собран на транспорте с гейтом, а гейт спрашивает
   * сессию, которую заводит метод того же клиента. Цикла в рантайме нет — `ensure()`
   * вызывается только из запроса, то есть после того, как обе строки ниже выполнились,
   * а сам `POST /session` гейт пропускает мимо (см. `withSession`).
   */
  let client: ClientApi;
  const session = createSessionController(() => client.startSession());
  const gated = withSession(transport, session);
  client = createClientApi(gated);

  return {
    // Наружу отдаётся транспорт С гейтом: кабинет бизнеса ходит по защищённым путям
    // тем же способом, и владелец, у которого не завёлся аккаунт, обязан увидеть причину
    // отказа входа, а не «организация не найдена» при попытке её создать.
    transport: gated,
    client,
    cabinet: createCabinetApi(gated),
    admin: createAdminApi(gated),
    session,
  };
}
